import React from 'react';
import { Link } from 'react-router-dom';
import { ScrollText, MapPin } from 'lucide-react';
import { ValidationBadge } from '../ValidationBadge';
import { coordLabel, mapPath, monsterPath, spotPath } from '../../../utils/worldFormat';
import type { QuestDropView } from '../../../utils/questView';
import { EntityThumb } from '../../world/EntityThumb';
import { QuestSoloNote } from './QuestSoloNote';
const SHOW = 6;

/**
 * Drop de misión: requiere la misión aceptada, clase elegible e inventario sin el objeto.
 * La tirada es condicional; no es una probabilidad final por muerte ni un drop general.
 */
export function QuestDropPanel({
  drop
}: {
  drop: QuestDropView;
}) {
  const shown = drop.targets.slice(0, SHOW);
  const rest = drop.targets.slice(SHOW);
  return <div className="mt-2 rounded-xl border border-emerald-300/20 bg-emerald-300/[0.04] p-3" data-id="element-1459" data-mp="1cvdfrppnldbg">
      <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-white" data-id="element-1460" data-mp="6augo2pd431d">
        <ScrollText className="h-4 w-4 text-emerald-200" aria-hidden="true" data-id="element-1461" data-mp="1cj6fqv5nqim" />
        Se obtiene durante la misión
        <ValidationBadge status="documentado" label="Confirmado por configuración" data-id="element-1462" data-mp="20q0do71wnoro" />
      </p>
      <QuestSoloNote data-id="element-1463" data-mp="25oldw6h5q8g4" />
      <ul className="mt-1.5 space-y-0.5 text-xs text-amber-100" data-id="element-1464" data-mp="1qtxacxvbhk35">
        {drop.conditions.map(c => <li key={c} data-id="element-1465" data-mp="1vsrv45cuhipw">· {c}</li>)}
      </ul>
      {drop.roll && <p className="mt-1.5 text-xs text-gray-300" data-id="element-1466" data-mp="1vqx88li0jgj6">
          Tirada de misión: <span className="font-semibold text-white" data-id="element-1467" data-mp="1i0bufb7f1ws5">{drop.roll}</span> por intento válido. No es la probabilidad final por muerte.
        </p>}
      {drop.targets.length > 0 ? <>
          <p className="mt-2 text-xs text-gray-400" data-id="element-1468" data-mp="1848nls1md9yv">{drop.targets.length} {drop.targets.length === 1 ? 'monstruo' : 'combinaciones de monstruo y mapa'}. Posibles, no garantizadas.</p>
          <ul className="mt-1.5 grid gap-1.5 sm:grid-cols-2" data-id="element-1469" data-mp="z290qj3xbbo0">{shown.map(t => <TargetRow key={`${t.monsterId}-${t.mapId ?? ''}`} t={t} data-id="element-1470" data-mp="1342zgluk65lj" />)}</ul>
          {rest.length > 0 && <details className="mt-1.5" data-id="element-1471" data-mp="35qhss6s6fdl5">
              <summary className="cursor-pointer text-xs font-semibold text-[#D4AF37]" data-id="element-1472" data-mp="35rjf57en7mi6">Ver {rest.length} más</summary>
              <ul className="mt-1.5 grid gap-1.5 sm:grid-cols-2" data-id="element-1473" data-mp="30yqccp2qio8q">{rest.map(t => <TargetRow key={`${t.monsterId}-${t.mapId ?? ''}`} t={t} data-id="element-1474" data-mp="2w1zwo2uudunu" />)}</ul>
            </details>}
        </> : <p className="mt-1.5 text-xs text-gray-400" data-id="element-1475" data-mp="2r3ojbi5c920v">Monstruos de esta regla pendientes de documentar.</p>}
    </div>;
}
function TargetRow({
  t
}: {
  t: QuestDropView['targets'][number];
}) {
  const spot = t.spawns[0];
  const coord = spot ? coordLabel(spot) : undefined;
  return <li className="flex gap-2.5 rounded-lg border border-white/10 bg-[#0A1628] px-2.5 py-2 text-xs" data-id="element-1476" data-mp="3epv3t3vy8a7z">
      <EntityThumb entity={{
      type: 'monster',
      id: t.monsterId
    }} name={t.monsterName} data-id="element-1477" data-mp="3apba6aal3oql" />
      <span className="min-w-0 flex-1" data-id="element-1478" data-mp="23d2h47i0mbys">
      <Link to={monsterPath(t.monsterId)} className="font-semibold text-white underline decoration-[#D4AF37]/40 underline-offset-4 hover:text-[#D4AF37]" data-id="element-1479" data-mp="239y54nyb6bxk">{t.monsterName}</Link>
      <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-gray-400" data-id="element-1480" data-mp="euhp8uhcfhej">
        <MapPin className="h-3 w-3 text-[#D4AF37]" aria-hidden="true" data-id="element-1481" data-mp="iurvm891s9tx" />
        {t.mapId !== undefined ? <Link to={mapPath(t.mapId)} className="hover:text-white" data-id="element-1482" data-mp="ns0fqedranxt">{t.mapName ?? `Mapa ${t.mapId}`}</Link> : <span data-id="element-1483" data-mp="slwal7yq3uor">Mapa no indicado</span>}
        {coord ? <Link to={spotPath(spot.id)} className="font-mono text-[11px] text-gray-300 hover:text-white" data-id="element-1484" data-mp="ximphryskk8l">· {coord}{t.spawns.length > 1 ? ` (+${t.spawns.length - 1})` : ''}</Link> : <span className="text-gray-500" data-id="element-1485" data-mp="16l4cw9q6nix6">· ubicación exacta no documentada</span>}
      </span>
      </span>
    </li>;
}